import {
  ActionIcon,
  Button,
  Group,
  InputWrapper,
  Modal,
  Radio,
  Space,
  Stack,
  Stepper,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  Tooltip,
  useMantineTheme,
  List,
} from "@mantine/core"
import { useForm, UseFormReturnType } from "@mantine/form"
import { useDisclosure } from "@mantine/hooks"
import { modals } from "@mantine/modals"
import { showNotification } from "@mantine/notifications"
import {
  IconArrowRight,
  IconBrandGoogleDrive,
  IconFileTypeCsv,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react"
import { zodResolver } from "mantine-form-zod-resolver"
import {
  createMRTColumnHelper,
  MantineReactTable,
  useMantineReactTable,
} from "mantine-react-table"
import { useCallback, useMemo, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { z, ZodSchema } from "zod"

import {
  AwsDatasourceCreationSchema,
  AwsDatasourceFormDataWithFileData,
  BQDatasourceFormData,
  bqImportParametersSchema,
  CsvDatasourceFormData,
  csvImportParametersSchema,
  CSVOrientation,
  DatadogCIVisibilityConfigurationSchema,
  DatadogCIVisibilityDatasourceFormData,
  DatadogCIVisibilityValidationSchema,
  DatadogConfigurationSchema,
  DatadogDatasourceFormData,
  DatadogMetadataUnion,
  DatadogValidationSeriesDataSchema,
  GCSDatasouceCreationFormData,
  GCSDatasouceCreationSchema,
  GSheetDatasourceFormData,
  gSheetImportParametersSchema,
  MetricsDatasourceFormData,
  MetricsDatasourceResponses,
  metricsDefinitionSchemaBase,
  metricsGeneralParametersSchema,
} from "@costory/types/endpoints/metricsDatasources"
import { MetricsDatasourceType } from "@costory/types/prisma-client"

import { DEFAULT_DATE_FORMAT } from "@costory/shared/const"
import dayjs from "@costory/shared/dayjs"

// Helper function to check if datasource is Datadog-related
const isDatadogType = (type: MetricsDatasourceType) =>
  type === MetricsDatasourceType.Datadog ||
  type === MetricsDatasourceType.DatadogCIVisibility

import {
  CsvSvgComponent,
  GsheetSvgComponent,
  DatadogSvgComponent,
  BigquerySvgComponent,
  AwsSvgComponent,
  GcsSvgComponent,
} from "@costory/front/components/DatasourceImages"
import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import { AwsColumnsMapping } from "@costory/front/pages/Datasources/components/AWS/AwsColumnsMapping"
import { AwsConfiguration } from "@costory/front/pages/Datasources/components/AWS/configuration"
import { AwsDocs } from "@costory/front/pages/Datasources/components/AWS/docs"
import { AwsValidation } from "@costory/front/pages/Datasources/components/AWS/validation"
import { BigQueryConfiguration } from "@costory/front/pages/Datasources/components/BigQuery/Configuration"
import { BigQueryDocs } from "@costory/front/pages/Datasources/components/BigQuery/Docs"
import { BigQueryValidation } from "@costory/front/pages/Datasources/components/BigQuery/Validation"
import { DatadogCIVisibilityDocs } from "@costory/front/pages/Datasources/components/DataDogCIVisibility/docs"
import { DatadogCIVisibilityValidation } from "@costory/front/pages/Datasources/components/DataDogCIVisibility/validation"
import { DatadogConfiguration } from "@costory/front/pages/Datasources/components/Datadog/configuration"
import { DatadogValidation } from "@costory/front/pages/Datasources/components/Datadog/validation"
import GCSConfiguration from "@costory/front/pages/Datasources/components/GCS/configuration"
import { GCSQueryValidation } from "@costory/front/pages/Datasources/components/GCS/validation"
import { MetricsGapConfig } from "@costory/front/pages/Datasources/components/MetricsSettings"
import { useBusinessMetricsQuery } from "@costory/front/queries/businessMetrics"
import {
  useCreateMetricsDatasourceMutation,
  useDeleteDatasourceMutation,
  useMetricsDatasourcesListQuery,
} from "@costory/front/queries/metricsDatasources"
import { PropsWithData } from "@costory/front/utils/propsWithData"
import { getDefaultTableOptions } from "@costory/front/utils/table"

import { CsvColumnMappingForm } from "./components/CSV/CsvMapping"
import { CSVDropStep } from "./components/CSV/DropStep"
import { CSVDocs } from "./components/CSV/docs"
import { DatadogCIVisibilityConfiguration } from "./components/DataDogCIVisibility/configuration"
import { DatadogDocs } from "./components/Datadog/docs"
import { GSheetImportStep } from "./components/GSheet/ImportStep"
import { GSheetParamStep } from "./components/GSheet/ParamsStep"
import { GSheetDocs } from "./components/GSheet/docs"

const _MetricsDatasourcesPage = ({
  data: datasources,
}: PropsWithData<MetricsDatasourceResponses.GetAllMetrics>) => {
  const [
    isImportModalOpen,
    { open: openImportModal, close: closeImportModal },
  ] = useDisclosure(false)

  const [searchParams, setSearchParams] = useSearchParams()
  const isDetailsModalOpen = searchParams.get("details") !== null
  const openDetailsModal = (id: string) => {
    setSearchParams({ details: id })
  }
  const closeDetailsModal = () => {
    setSearchParams({})
  }

  const { mutateAsync: deleteDatasource, isPending: isDeleting } =
    useDeleteDatasourceMutation()
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const { data: metrics } = useBusinessMetricsQuery()

  const isDatasourceUsed = (datasourceId: string) =>
    metrics?.some((metric) => {
      return (
        !metric.technical &&
        metric.datasource != null &&
        "id" in metric.datasource &&
        metric.datasource.id === datasourceId
      )
    })

  const openDeleteModal = useCallback(
    (toDelete: MetricsDatasourceResponses.GetMetric) =>
      modals.openConfirmModal({
        title: "Please confirm you want to delete " + toDelete.name,
        labels: { confirm: "Confirm", cancel: "Cancel" },
        onConfirm: () => {
          setDeletingId(toDelete.id)
          deleteDatasource(toDelete.id).finally(() => setDeletingId(null))
        },
      }),
    [deleteDatasource],
  )

  const columns = useMemo(() => {
    const columnHelper =
      createMRTColumnHelper<MetricsDatasourceResponses.GetMetric>()
    return [
      columnHelper.accessor("type", {
        header: "Type",
        enableColumnActions: false,
        enableSorting: false,
        Header: () => null,
        Cell: ({ cell }) => {
          const datasource = availableDatasources().find(
            ({ value }) => value === cell.getValue(),
          )

          if (!datasource) {
            return null
          }

          const { icon: IconComponent, label } = datasource

          return (
            <Group align="center">
              <Tooltip label={label}>
                <ThemeIcon size={50}>{IconComponent}</ThemeIcon>
              </Tooltip>
            </Group>
          )
        },
      }),
      columnHelper.accessor("name", {
        header: "Name",
        filterVariant: "select",
      }),
      columnHelper.accessor("updatedAt", {
        header: "Last Update",
        Cell: ({ cell }) => dayjs(cell.getValue()).format(DEFAULT_DATE_FORMAT),
      }),
    ]
  }, [])
  const navigate = useNavigate()
  const table = useMantineReactTable<MetricsDatasourceResponses.GetMetric>({
    columns,
    data: datasources,
    enableRowActions: true,
    positionActionsColumn: "last",
    renderRowActions: ({ row }) => (
      <>
        <Tooltip
          multiline
          label={(() => {
            const usedBy =
              metrics?.filter(
                (metric) =>
                  !metric.technical &&
                  metric.datasource &&
                  "id" in metric.datasource &&
                  metric.datasource.id === row.original.id,
              ) || []
            if (usedBy.length > 0) {
              return (
                <div>
                  <Text>
                    This datasource is in use by the following{" "}
                    {usedBy.length === 1 ? "formula" : "formulas"}:
                  </Text>
                  <List spacing="xs" withPadding style={{ margin: 0 }}>
                    {usedBy.map((metric) => (
                      <List.Item key={metric.id}>{metric.name}</List.Item>
                    ))}
                  </List>
                </div>
              )
            }
            return "Delete datasource"
          })()}
        >
          <ActionIcon
            onClick={() => {
              if (!isDatasourceUsed(row.original.id)) {
                openDeleteModal(row.original)
              }
            }}
            loading={deletingId === row.original.id && isDeleting}
            disabled={isDatasourceUsed(row.original.id)}
            color={isDatasourceUsed(row.original.id) ? "gray" : "red"}
            data-intercom-target="costory-tour-datasource-metrics-delete-button"
          >
            <IconTrash />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Create new formula from this datasource">
          <ActionIcon
            onClick={() => {
              navigate(`/formulas/new/${row.original.id}`)
            }}
            data-intercom-target="costory-tour-datasource-metrics-create-formula-button"
          >
            <IconPlus />
          </ActionIcon>
        </Tooltip>
      </>
    ),
    ...getDefaultTableOptions(),
    mantineTableBodyRowProps: ({ row: { original: datasource } }) => ({
      onDoubleClick: () => {
        openDetailsModal(datasource.id)
      },
    }),
  })

  return (
    <Stack>
      <Group justify="flex-end">
        <Button
          onClick={() => {
            // setSelectedRow(undefined)
            openImportModal()
          }}
          leftSection={<IconPlus />}
          data-intercom-target="costory-tour-datasource-metrics-add-button"
        >
          Import new metrics datasource
        </Button>
      </Group>

      <MantineReactTable table={table} />
      <Modal
        classNames={{ content: "custom-modal-content" }}
        opened={isImportModalOpen}
        onClose={closeImportModal}
        size="70vw"
        title={<Title order={2}>Import new metrics datasource</Title>}
      >
        <Modal.Body h="70vh">
          <AddMetricsDatasourceFunnel onFinish={closeImportModal} />
        </Modal.Body>
      </Modal>
      <Modal
        opened={isDetailsModalOpen}
        onClose={closeDetailsModal}
        size="70vw"
      >
        <MetricsDatasourceDetails
          metricsDatasource={
            datasources.find(({ id }) => id === searchParams.get("details"))!
          }
        />
      </Modal>
    </Stack>
  )
}

const MetricsDatasourceDetails = ({
  metricsDatasource,
}: {
  metricsDatasource: MetricsDatasourceResponses.GetMetric
}) => {
  useMantineTheme()
  if (!metricsDatasource) {
    return null
  }

  return (
    <Stack>
      <Group>
        {metricsDatasource.type === MetricsDatasourceType.CSV && (
          <ThemeIcon size={70} variant="light" color="textMuted">
            <IconFileTypeCsv size={50} />
          </ThemeIcon>
        )}
        {metricsDatasource.type === MetricsDatasourceType.GoogleSheet && (
          <ThemeIcon size={70} variant="light" color="textMuted">
            <IconBrandGoogleDrive size={50} />
          </ThemeIcon>
        )}
        {isDatadogType(metricsDatasource.type) && <DatadogSvgComponent />}
        <Stack gap={0}>
          <Title>Metrics datasource {metricsDatasource.name}</Title>
          <Group>
            <Text fw="bold">Owner: {metricsDatasource.owner}</Text>
            <Text>
              Last update:{" "}
              {dayjs(metricsDatasource.updatedAt).format("DD/MM/YYYY")}
            </Text>
          </Group>
        </Stack>
      </Group>
      <Space h={20} />
      <Title order={3}>Parameters</Title>
      {metricsDatasource.type === MetricsDatasourceType.CSV && (
        <>
          <InputWrapper label="Data orientation">
            <TextInput
              value={metricsDatasource.metadata.orientation}
              readOnly
            />
          </InputWrapper>
          <InputWrapper label="Dates column">
            <TextInput value={metricsDatasource.metadata.date} readOnly />
          </InputWrapper>
          {metricsDatasource.metadata.orientation === CSVOrientation.Rows && (
            <>
              <InputWrapper label="Metrics names column">
                <TextInput
                  value={metricsDatasource.metadata.metricNames}
                  readOnly
                />
              </InputWrapper>
              <InputWrapper label="Metrics values column">
                <TextInput
                  value={metricsDatasource.metadata.metricValues}
                  readOnly
                />
              </InputWrapper>
            </>
          )}
        </>
      )}
      {metricsDatasource.type === MetricsDatasourceType.GoogleSheet && (
        <>
          <Button
            onClick={() => {
              window.open(
                `https://docs.google.com/spreadsheets/d/${metricsDatasource.metadata.gSheetId}`,
                "_blank",
              )
            }}
            leftSection={<IconBrandGoogleDrive />}
            data-intercom-target="costory-tour-datasource-metrics-open-gsheet-button"
          >
            Open in Google Drive
          </Button>
          <InputWrapper label="Google Sheet">
            <TextInput value={metricsDatasource.metadata.gSheetId} readOnly />
          </InputWrapper>
        </>
      )}
      {isDatadogType(metricsDatasource.type) && (
        <>
          <InputWrapper label="Datadog query">
            <TextInput
              value={(metricsDatasource.metadata as DatadogMetadataUnion).query}
              readOnly
            />
          </InputWrapper>
        </>
      )}
    </Stack>
  )
}

export const MetricsDatasourcesPage = () => {
  const datasourceQuery = useMetricsDatasourcesListQuery()

  return (
    <QueryWrapper query={datasourceQuery} allowEmptyArray>
      {_MetricsDatasourcesPage}
    </QueryWrapper>
  )
}

interface AddMetricsDatasourceFunnelProps {
  onFinish: () => void
}
export const AddMetricsDatasourceFunnel = ({
  onFinish,
}: AddMetricsDatasourceFunnelProps) => {
  const [activeStep, setActiveStep] = useState(0)
  const { mutateAsync: createDatasource, isPending: isImporting } =
    useCreateMetricsDatasourceMutation()

  const form = useForm<MetricsDatasourceFormData>({
    validate: (values) => {
      if (activeStep === 0) {
        return zodResolver(metricsGeneralParametersSchema)(values)
      }
      if (activeStep === 1) {
        if (type === MetricsDatasourceType.Datadog) {
          return zodResolver(DatadogConfigurationSchema)(values)
        }
        if (type === MetricsDatasourceType.DatadogCIVisibility) {
          return zodResolver(DatadogCIVisibilityConfigurationSchema)(values)
        }
        if (type === MetricsDatasourceType.GCS) {
          return zodResolver(GCSDatasouceCreationSchema)(values)
        }
        return {}
      }
      if (activeStep == 3) {
        return zodResolver(importParametersSchemasFinalMap[values.type!])(
          values,
        )
      }
      return zodResolver(importParametersSchemasMap[values.type!])(values)
    },
  })

  const handleClickNext = () =>
    setActiveStep((current) => {
      if (form.validate().hasErrors) {
        return current
      }
      return current < 4 ? current + 1 : current
    })
  const handleClickBack = () => {
    if (form.values.type == MetricsDatasourceType.AWS && activeStep === 3) {
      form.setValues((prev) => ({
        ...prev,
        metricsNames: [],
        metricsDefinition: [],
      }))
    }
    setActiveStep((prev) => (prev > 0 ? prev - 1 : prev))
  }

  const handleClickImport = async () => {
    if (form.validate().hasErrors) {
      return
    }

    try {
      await createDatasource(form.getValues())

      onFinish()
    } catch {
      showNotification({
        title: "Error",
        message: "Failed to create datasource",
        color: "red",
      })
    }
  }

  const { type } = form.getValues()
  const [metricsNames, setMetricsNames] = useState<string[]>([])
  return (
    <Stack h="100%" justify="space-between">
      <Stepper
        active={activeStep}
        onStepClick={setActiveStep}
        allowNextStepsSelect={false}
        h="100%"
        styles={{
          content: {
            height: "95%",
          },
          stepIcon: {
            backgroundColor: "var(--mantine-color-primaryHighlight-0)",
            color: "var(--mantine-color-textOnError-0)",
          },
        }}
      >
        <Stepper.Step label={<Text fw="bold">Select datasource</Text>}>
          <Stack h="100%">
            <Stack flex={1}>
              <Group align="flex-start">
                <InputWrapper
                  label={
                    <Text fw="bold" span={true}>
                      Name
                    </Text>
                  }
                  required
                  w="40%"
                >
                  <TextInput
                    {...form.getInputProps("name")}
                    w="100%"
                    data-intercom-target="costory-tour-datasource-metrics-name-input"
                  />
                </InputWrapper>
              </Group>
              <InputWrapper
                label={
                  <Text fw="bold" span={true}>
                    Datasource type
                  </Text>
                }
                required
              >
                <Radio.Group
                  {...form.getInputProps("type")}
                  data-intercom-target="costory-tour-datasource-metrics-type-select"
                >
                  <Group align="left">
                    {availableDatasources().map(({ label, value, icon }) => {
                      const isSelected = type === value
                      const IconComponent = icon
                      return (
                        <Radio.Card
                          key={value}
                          value={value}
                          w={180}
                          h={180}
                          p={8}
                          radius="md"
                          withBorder
                          style={{
                            cursor: "pointer",
                            display: "flex",
                            flexDirection: "column",
                            borderColor: isSelected
                              ? "var(--mantine-color-primaryHighlight-0)"
                              : "var(--mantine-color-border-0)",
                            borderWidth: isSelected ? 2 : 1,
                          }}
                          data-intercom-target={`costory-tour-datasource-metrics-${value.toLowerCase().replace(/\s+/g, "-")}-option`}
                        >
                          <Stack
                            justify="center"
                            align="center"
                            w="100%"
                            h="100%"
                          >
                            {IconComponent}
                            <Text fw="bold">{label}</Text>
                          </Stack>
                        </Radio.Card>
                      )
                    })}
                  </Group>
                </Radio.Group>
              </InputWrapper>
            </Stack>
            {type === MetricsDatasourceType.CSV && <CSVDocs />}
            {type === MetricsDatasourceType.GoogleSheet && <GSheetDocs />}
            {type === MetricsDatasourceType.Datadog && <DatadogDocs />}
            {type === MetricsDatasourceType.DatadogCIVisibility && (
              <DatadogCIVisibilityDocs />
            )}
            {type === MetricsDatasourceType.AWS && <AwsDocs />}
            {type === MetricsDatasourceType.BigQuery && <BigQueryDocs />}
            <Group justify="flex-end" pb={20}>
              <Button
                onClick={handleClickNext}
                rightSection={<IconArrowRight />}
                data-intercom-target="costory-tour-datasource-metrics-next-button"
              >
                Next
              </Button>
            </Group>
          </Stack>
        </Stepper.Step>

        {type === MetricsDatasourceType.Datadog && (
          <Stepper.Step label={<Text fw="bold">Configuration</Text>}>
            <DatadogConfiguration
              form={form as UseFormReturnType<DatadogDatasourceFormData>}
              handleClickBack={handleClickBack}
              handleClickNext={handleClickNext}
            />
          </Stepper.Step>
        )}
        {type === MetricsDatasourceType.DatadogCIVisibility && (
          <Stepper.Step label={<Text fw="bold">Configuration</Text>}>
            <DatadogCIVisibilityConfiguration
              form={
                form as UseFormReturnType<DatadogCIVisibilityDatasourceFormData>
              }
              handleClickBack={handleClickBack}
              handleClickNext={handleClickNext}
            />
          </Stepper.Step>
        )}
        {type === MetricsDatasourceType.AWS && (
          <Stepper.Step label={<Text fw="bold">Configuration</Text>}>
            <AwsConfiguration
              form={
                form as UseFormReturnType<AwsDatasourceFormDataWithFileData>
              }
              handleClickNext={handleClickNext}
              handleClickBack={handleClickBack}
            />
          </Stepper.Step>
        )}
        {type === MetricsDatasourceType.Datadog && (
          <Stepper.Step label={<Text fw="bold">Validation</Text>}>
            <DatadogValidation
              form={form as UseFormReturnType<DatadogDatasourceFormData>}
              handleClickBack={handleClickBack}
              handleClickNext={handleClickNext}
              setMetricsNames={setMetricsNames}
            />
          </Stepper.Step>
        )}
        {type === MetricsDatasourceType.DatadogCIVisibility && (
          <Stepper.Step label={<Text fw="bold">Validation</Text>}>
            <DatadogCIVisibilityValidation
              form={
                form as UseFormReturnType<DatadogCIVisibilityDatasourceFormData>
              }
              handleClickBack={handleClickBack}
              handleClickNext={handleClickNext}
              setMetricsNames={setMetricsNames}
            />
          </Stepper.Step>
        )}
        {type === MetricsDatasourceType.BigQuery && (
          <Stepper.Step label={<Text fw="bold">Configuration</Text>}>
            <BigQueryConfiguration
              form={form as UseFormReturnType<BQDatasourceFormData>}
              handleClickBack={handleClickBack}
              handleClickNext={handleClickNext}
            />
          </Stepper.Step>
        )}

        {type === MetricsDatasourceType.GCS && (
          <Stepper.Step label={<Text fw="bold">Configuration</Text>}>
            <GCSConfiguration
              form={form as UseFormReturnType<GCSDatasouceCreationFormData>}
              handleClickBack={handleClickBack}
              handleClickNext={handleClickNext}
            />
          </Stepper.Step>
        )}
        {type !== MetricsDatasourceType.Datadog &&
          type !== MetricsDatasourceType.DatadogCIVisibility &&
          type !== MetricsDatasourceType.BigQuery &&
          type !== MetricsDatasourceType.AWS &&
          type !== MetricsDatasourceType.GCS && (
            <Stepper.Step label={<Text fw="bold">File import</Text>}>
              {type === MetricsDatasourceType.CSV && (
                <CSVDropStep
                  form={form}
                  handleClickBack={handleClickBack}
                  handleClickNext={handleClickNext}
                />
              )}
              {type === MetricsDatasourceType.GoogleSheet && (
                <GSheetParamStep
                  form={form as UseFormReturnType<GSheetDatasourceFormData>}
                  handleClickBack={handleClickBack}
                  handleClickNext={handleClickNext}
                />
              )}
            </Stepper.Step>
          )}
        {type === MetricsDatasourceType.AWS && (
          <Stepper.Step label={<Text fw="bold">Validation</Text>}>
            <AwsValidation
              form={
                form as UseFormReturnType<AwsDatasourceFormDataWithFileData>
              }
              handleClickBack={handleClickBack}
              handleClickNext={handleClickNext}
            />
          </Stepper.Step>
        )}
        {type === MetricsDatasourceType.BigQuery && (
          <Stepper.Step label={<Text fw="bold">Validation</Text>}>
            <BigQueryValidation
              form={form as UseFormReturnType<BQDatasourceFormData>}
              handleClickBack={handleClickBack}
              handleClickNext={handleClickNext}
            />
          </Stepper.Step>
        )}
        {type === MetricsDatasourceType.GCS && (
          <Stepper.Step label={<Text fw="bold">Validation</Text>}>
            <GCSQueryValidation
              form={form as UseFormReturnType<GCSDatasouceCreationFormData>}
              handleClickBack={handleClickBack}
              handleClickImport={handleClickImport}
              isImporting={isImporting}
            />
          </Stepper.Step>
        )}
        {type !== MetricsDatasourceType.Datadog &&
          type !== MetricsDatasourceType.BigQuery &&
          type !== MetricsDatasourceType.DatadogCIVisibility &&
          type !== MetricsDatasourceType.GCS && (
            <Stepper.Step label={<Text fw="bold">Columns mapping</Text>}>
              {type === MetricsDatasourceType.CSV && (
                <CsvColumnMappingForm
                  form={form as UseFormReturnType<CsvDatasourceFormData>}
                  handleClickBack={handleClickBack}
                  handleClickImport={handleClickNext}
                />
              )}
              {type === MetricsDatasourceType.GoogleSheet && (
                <GSheetImportStep
                  form={form as UseFormReturnType<GSheetDatasourceFormData>}
                  handleClickBack={handleClickBack}
                  handleClickNext={handleClickNext}
                />
              )}
              {type === MetricsDatasourceType.AWS && (
                <AwsColumnsMapping
                  form={
                    form as UseFormReturnType<AwsDatasourceFormDataWithFileData>
                  }
                  handleClickBack={handleClickBack}
                  handleClickImport={handleClickNext}
                />
              )}
            </Stepper.Step>
          )}
        {type !== MetricsDatasourceType.GCS && (
          <Stepper.Step
            label={<Text fw="bold">Metrics gap configuration</Text>}
          >
            <MetricsGapConfig
              form={form}
              handleClickBack={handleClickBack}
              handleClickImport={handleClickImport}
              isImporting={isImporting}
              metricsNames={metricsNames}
            />
          </Stepper.Step>
        )}
      </Stepper>
    </Stack>
  )
}

const availableDatasources = () => [
  {
    label: "CSV",
    value: MetricsDatasourceType.CSV,
    icon: <CsvSvgComponent />,
  },
  {
    label: "Google Sheet",
    value: MetricsDatasourceType.GoogleSheet,
    icon: <GsheetSvgComponent />,
  },
  {
    label: "Datadog",
    value: MetricsDatasourceType.Datadog,
    icon: <DatadogSvgComponent />,
  },
  {
    label: "Datadog CI Visibility",
    value: MetricsDatasourceType.DatadogCIVisibility,
    icon: <DatadogSvgComponent />,
  },

  {
    label: "Parquet file (AWS S3)",
    value: MetricsDatasourceType.AWS,
    icon: <AwsSvgComponent />,
  },

  {
    label: "Big Query",
    value: MetricsDatasourceType.BigQuery,
    icon: <BigquerySvgComponent />,
  },
  {
    label: "GCS Inventory (Parquet)",
    value: MetricsDatasourceType.GCS,
    icon: <GcsSvgComponent />,
  },
]

const importParametersSchemasMap = {
  [MetricsDatasourceType.CSV]: csvImportParametersSchema.superRefine(
    ({ orientation, metricNames, metricValues }, ctx) => {
      if (orientation === CSVOrientation.Rows && !metricValues) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Required",
          path: ["metricValues"],
        })
      }
      if (orientation === CSVOrientation.Rows && !metricNames) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Required",
          path: ["metricNames"],
        })
      }
    },
  ),
  [MetricsDatasourceType.GoogleSheet]: gSheetImportParametersSchema,
  [MetricsDatasourceType.Datadog]: DatadogValidationSeriesDataSchema,
  [MetricsDatasourceType.BigQuery]: bqImportParametersSchema,
  [MetricsDatasourceType.DatadogCIVisibility]:
    DatadogCIVisibilityValidationSchema,
  [MetricsDatasourceType.AWS]: AwsDatasourceCreationSchema,
  [MetricsDatasourceType.GCS]: GCSDatasouceCreationSchema,
} as const satisfies Record<MetricsDatasourceType, ZodSchema>

const importParametersSchemasFinalMap = {
  [MetricsDatasourceType.CSV]: csvImportParametersSchema
    .extend({ metricsDefinition: metricsDefinitionSchemaBase })
    .superRefine(({ orientation, metricNames, metricValues }, ctx) => {
      if (orientation === CSVOrientation.Rows && !metricValues) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Required",
          path: ["metricValues"],
        })
      }
      if (orientation === CSVOrientation.Rows && !metricNames) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Required",
          path: ["metricNames"],
        })
      }
    }),
  [MetricsDatasourceType.GoogleSheet]: gSheetImportParametersSchema.extend({
    metricsDefinition: metricsDefinitionSchemaBase,
  }),
  [MetricsDatasourceType.BigQuery]: bqImportParametersSchema.extend({
    metricsDefinition: metricsDefinitionSchemaBase,
  }),
  [MetricsDatasourceType.Datadog]: DatadogValidationSeriesDataSchema.extend({
    metricsDefinition: metricsDefinitionSchemaBase,
  }),
  [MetricsDatasourceType.DatadogCIVisibility]:
    DatadogCIVisibilityValidationSchema.extend({
      metricsDefinition: metricsDefinitionSchemaBase,
    }),
  [MetricsDatasourceType.AWS]: AwsDatasourceCreationSchema,
  [MetricsDatasourceType.GCS]: GCSDatasouceCreationSchema,
} as const satisfies Record<MetricsDatasourceType, ZodSchema>
